import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { Link, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import RepeatIcon from '@mui/icons-material/Repeat';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InboxIcon from '@mui/icons-material/Inbox';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PageTitle from '../../components/PageTitle';
import MUIModal from '../../components/MuiModal';
import Loader from '../../common/Loader';
import CopyIcon from '../../common/CopyIcon';
import { apiConstants, getGeneric } from '../../common/constants';
import KpiCard from './shared/KpiCard';
import FilterBar from './shared/FilterBar';
import PageBanner from './shared/PageBanner';
import ExportButton from './shared/ExportButton';
import ExpandableAppointmentsTable from './shared/ExpandableAppointmentsTable';
import useAppointmentSearch from './shared/useAppointmentSearch';
import {
    buildOverdueFilter, NO_NEW_DATE_FILTER, NO_NEW_DATE_AGE_BANDS, ageBandFilter, getClosureCategoryMeta, lastMiss,
} from './shared/constants';
import SectionTitle from './shared/SectionTitle';
import ShareBar, { PctChip } from './shared/ShareBar';
import { TOKEN_COLOR } from './shared/tokens';
import AppointmentDelivery from '../B2BOrder/AddAppDelivery';
import MarkMissedModal from './modals/MarkMissedModal';

const textSx = { fontSize: '12px', color: '#333', lineHeight: 1.5 };
const subSx = { fontSize: '11px', color: '#888', lineHeight: 1.5 };
const chipSx = (bg, color) => ({
    height: 20, fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px', borderRadius: '12px',
    bgcolor: bg, color, '& .MuiChip-label': { px: '10px', py: 0 },
});
const iconBtnSx = (bg, color) => ({
    width: 30, height: 30, borderRadius: '6px', bgcolor: bg, color,
    '&:hover': { bgcolor: bg, filter: 'brightness(0.95)' }, '& svg': { fontSize: 16 },
});

// Oldest slot first, so the longest-pending appointments are on top.
const OVERDUE_SORT = { field: 'appointmentDeliveryData.appointmentDeliveryEndTime', direction: 'ASC' };
// Ascending on an array sorts by its earliest entry, i.e. shipments missed longest ago first.
const NO_NEW_DATE_SORT = { field: 'appointmentDeliveryHistory.appointmentDeliveryStartTime', direction: 'ASC' };

// KPI tiles; each one also filters the list. NO_NEW_DATE and the age bands list a different set of shipments
// (missed with a reason, no next appointment), so they bring their own filter, sort and columns.
const TILES = { ALL: 'all', REPEAT: 'repeat', NO_KAM: 'noKam', NO_NEW_DATE: 'noNewDate' };
const AGE_BAND_BY_KEY = NO_NEW_DATE_AGE_BANDS.reduce((acc, b) => ({ ...acc, [b.key]: b }), {});
const isNoNewDateTile = (tile) => tile === TILES.NO_NEW_DATE || !!AGE_BAND_BY_KEY[tile];

// Days since the last missed appointment date (calendar days).
const daysSinceMiss = (row, now) => {
    const d = lastMiss(row)?.appointment_del_start;
    return d ? now.startOf('day').diff(dayjs(d).startOf('day'), 'day') : null;
};
const ageBandOf = (days) => NO_NEW_DATE_AGE_BANDS.find((b) => days >= b.minDays && (b.maxDays == null || days <= b.maxDays));

// Extra filter of an overdue tile, on top of the overdue rule. `mappedMerchantIds`: merchants that have a KAM.
const tileFilter = (tile, mappedMerchantIds) => {
    if (tile === TILES.REPEAT) return [{ field: 'appointmentDeliveryHistoryCount', operator: 'gte', value: 1 }];
    if (tile === TILES.NO_KAM) {
        return [{ field: 'merchant.$id', operator: 'nin', value: mappedMerchantIds.map((id) => `ObjectId('${id}')`) }];
    }
    return [];
};

const fmtSlot = (start, end) => {
    if (!start) return '-';
    const s = dayjs(start);
    const date = s.format(s.year() === dayjs().year() ? 'D MMM' : 'D MMM YYYY');
    return end ? `${date}, ${s.format('h:mm A')} – ${dayjs(end).format('h:mm A')}` : `${date}, ${s.format('h:mm A')}`;
};

// Same appointment number rule as the listing: stored miss count, else history size, plus the current one.
const appointmentNo = (row) => (row?.appointmentDeliveryHistoryCount ?? row?.appointmentDeliveryHistory?.length ?? 0) + 1;

// Who booked the current appointment (set once when it is scheduled; edits do not change it).
const bookedBy = (row) => {
    const first = row?.appointmentDeliveryData?.modifiedBy?.[0];
    return { name: first?.name || row?.appointmentDeliveryData?.modifiedByUser || '', email: first?.email || '' };
};

const merchantColumn = {
    field: 'merchant', headerName: 'Merchant', width: '11%',
    renderCell: ({ row }) => <Typography sx={{ ...textSx, fontWeight: 700 }}>{row?.merchantDetail?.name || '-'}</Typography>,
};

const awbColumn = {
    field: 'awb', headerName: 'AWB / Ref', width: '14%',
    renderCell: ({ row }) => (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ ...textSx, fontWeight: 700, wordBreak: 'break-all' }}>{row?.awb_number || '-'}</Typography>
                {row?.awb_number ? <CopyIcon text={row.awb_number} /> : null}
            </Box>
            {row?.reference && <Typography sx={{ ...subSx, wordBreak: 'break-word' }}>Ref {row.reference}</Typography>}
            <Typography sx={subSx}>{row?.courierDetail?.parent || ''}</Typography>
        </Box>
    ),
};

const kamColumn = ({ kamByMerchant, kamError }) => ({
    field: 'kamOwner', headerName: 'KAM', width: '13%',
    renderCell: ({ row }) => {
        if (kamError) return <Typography sx={subSx}>Couldn't load</Typography>;
        const kam = kamByMerchant[row?.merchantDetail?.key];
        if (!kam) return <Typography sx={{ ...subSx, fontStyle: 'italic' }}>Not mapped</Typography>;
        return (
            <Box>
                <Typography sx={{ ...textSx, wordBreak: 'break-word' }}>{kam.kamName}</Typography>
                {!kam.kamActive && <Typography sx={{ ...subSx, color: '#e65100' }}>Inactive KAM</Typography>}
            </Box>
        );
    },
});

const OrderDetailsButton = ({ row }) => (
    <Tooltip title='Order Details'>
        <IconButton component={Link} to={`/order-detail/${row?.awb_number}`} size='small' sx={iconBtnSx('#e3f2fd', '#1565c0')}>
            <VisibilityIcon />
        </IconButton>
    </Tooltip>
);

const buildColumns = ({ kamByMerchant, kamError, now, onMarkMissed }) => [
    merchantColumn,
    awbColumn,
    {
        field: 'appointment', headerName: 'Appointment', width: '17%',
        renderCell: ({ row }) => {
            const d = row?.appointmentDeliveryData || {};
            return (
                <Box>
                    <Typography sx={{ ...textSx, fontWeight: 600 }}>{fmtSlot(d.appointment_del_start, d.appointment_del_end)}</Typography>
                    <Typography sx={subSx}>Appt #{appointmentNo(row)}</Typography>
                </Box>
            );
        },
    },
    {
        field: 'status', headerName: 'Status', width: '13%',
        renderCell: ({ row }) => (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                <Tooltip title='Appointment status'>
                    <Chip size='small' label='OPEN · NOT CLOSED' sx={chipSx('#ffebee', '#c62828')} />
                </Tooltip>
                <Tooltip title='Shipment status'>
                    <Chip size='small' label={String(row?.orderStatus || '-').replace(/_/g, ' ')} sx={{ ...chipSx('#eceff1', '#546e7a'), maxWidth: '100%' }} />
                </Tooltip>
            </Box>
        ),
    },
    {
        field: 'pending', headerName: 'Pending', width: '10%',
        renderCell: ({ row }) => {
            const end = row?.appointmentDeliveryData?.appointment_del_end;
            if (!end) return <Typography sx={textSx}>-</Typography>;
            const days = now.startOf('day').diff(dayjs(end).startOf('day'), 'day');
            return (
                <Box>
                    <Typography sx={{ ...textSx, fontWeight: 700, color: '#c62828' }}>
                        {days <= 0 ? 'Today' : `${days} day${days === 1 ? '' : 's'}`}
                    </Typography>
                    <Typography sx={subSx}>since slot end</Typography>
                </Box>
            );
        },
    },
    {
        field: 'opsOwner', headerName: 'Booked By (Ops)', width: '13%',
        renderCell: ({ row }) => {
            const b = bookedBy(row);
            return (
                <Box>
                    <Typography sx={{ ...textSx, wordBreak: 'break-word' }}>{b.name || b.email || '-'}</Typography>
                    {b.email && b.email !== b.name && <Typography sx={{ ...subSx, wordBreak: 'break-word' }}>{b.email}</Typography>}
                </Box>
            );
        },
    },
    kamColumn({ kamByMerchant, kamError }),
    {
        field: 'action', headerName: 'Actions', width: '9%',
        renderCell: ({ row }) => (
            <Box sx={{ display: 'flex', gap: 0.75 }}>
                <Tooltip title='Mark as Missed (record the failure reason)'>
                    <IconButton size='small' onClick={() => onMarkMissed(row)} sx={iconBtnSx('#fff3e0', '#e65100')}>
                        <CancelIcon />
                    </IconButton>
                </Tooltip>
                <OrderDetailsButton row={row} />
            </Box>
        ),
    },
];

// Columns for "Missed, no new date": the last miss, its reason, how long ago, and booking the next appointment.
const buildNoNewDateColumns = ({ kamByMerchant, kamError, now, onSchedule }) => [
    merchantColumn,
    awbColumn,
    {
        field: 'lastMiss', headerName: 'Last Missed Appt', width: '15%',
        renderCell: ({ row }) => (
            <Box>
                <Typography sx={{ ...textSx, fontWeight: 600 }}>{fmtSlot(lastMiss(row)?.appointment_del_start)}</Typography>
                <Typography sx={subSx}>Appt #{row?.appointmentDeliveryHistoryCount ?? '-'} · missed</Typography>
            </Box>
        ),
    },
    {
        field: 'latestRemark', headerName: 'Latest Remark', width: '13%',
        renderCell: ({ row }) => {
            const meta = getClosureCategoryMeta(lastMiss(row)?.appointment_closure_category);
            return meta
                ? <Chip size='small' label={meta.label.toUpperCase()} sx={{ ...chipSx(meta.bg, meta.color), maxWidth: '100%' }} />
                : <Typography sx={subSx}>-</Typography>;
        },
    },
    {
        field: 'misses', headerName: 'Misses', width: '7%',
        renderCell: ({ row }) => <Typography sx={{ ...textSx, fontWeight: 700 }}>{row?.appointmentDeliveryHistoryCount ?? 0}×</Typography>,
    },
    {
        field: 'daysSince', headerName: 'Days Since Miss', width: '10%',
        renderCell: ({ row }) => {
            const days = daysSinceMiss(row, now);
            if (days == null) return <Typography sx={textSx}>-</Typography>;
            return (
                <Box>
                    <Typography sx={{ ...textSx, fontWeight: 800, fontSize: '15px', color: ageBandOf(days)?.color || TOKEN_COLOR.text }}>{days}</Typography>
                    <Typography sx={subSx}>days since miss</Typography>
                </Box>
            );
        },
    },
    kamColumn({ kamByMerchant, kamError }),
    {
        field: 'action', headerName: 'Actions', width: '13%',
        renderCell: ({ row }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Button size='small' variant='contained' onClick={() => onSchedule(row)}
                    sx={{ fontSize: '11px', fontWeight: 700, textTransform: 'none', boxShadow: 'none', whiteSpace: 'nowrap' }}>
                    Book next appt
                </Button>
                <OrderDetailsButton row={row} />
            </Box>
        ),
    },
];

// Overdue Appointments: ABD appointments still open on any day after the appointment date, i.e. never marked
// as missed with a reason. Closing one (Mark as Missed) removes it from this list.
export default function OverdueAppointments() {
    const [filters, setFilters] = useState({});
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(50);
    const [reload, setReload] = useState(0);
    const [missRow, setMissRow] = useState(null);
    const [scheduleRow, setScheduleRow] = useState(null);
    const [kamOwners, setKamOwners] = useState([]);
    const [kamError, setKamError] = useState(false);
    const [kamLoaded, setKamLoaded] = useState(false);
    const [searchParams] = useSearchParams();
    // Missed & Repeat links here with ?view=<tile> (e.g. all, noNewDate) to open that tile's list.
    const [tile, setTile] = useState(() => {
        const view = searchParams.get('view');
        return Object.values(TILES).includes(view) || AGE_BAND_BY_KEY[view] ? view : TILES.ALL;
    });
    // Fixed per load/refresh so the filter (and the query) does not change on every render.
    const [now, setNow] = useState(() => dayjs());
    const overdueFilter = useMemo(() => buildOverdueFilter(now.startOf('day')), [now]);

    useEffect(() => {
        getGeneric(apiConstants.APPOINTMENT_DELIVERY_KAM_OWNERS, {})
            .then((res) => { setKamOwners(Array.isArray(res?.data) ? res.data : []); setKamError(false); })
            .catch(() => setKamError(true))
            .finally(() => setKamLoaded(true));
    }, []);
    const kamByMerchant = useMemo(
        () => kamOwners.reduce((acc, k) => ({ ...acc, [k.merchantOid]: k }), {}),
        [kamOwners],
    );
    const mappedMerchantIds = useMemo(() => Object.keys(kamByMerchant), [kamByMerchant]);
    const todayStart = now.startOf('day');
    const filterFor = (t) => {
        if (!isNoNewDateTile(t)) return [...overdueFilter, ...tileFilter(t, mappedMerchantIds)];
        const band = AGE_BAND_BY_KEY[t];
        return [...NO_NEW_DATE_FILTER, ...(band ? ageBandFilter(band, todayStart) : [])];
    };
    const listFilter = filterFor(tile);
    const noNewDateView = isNoNewDateTile(tile);

    const { data: searchData, isFetching } = useAppointmentSearch({
        filters,
        extraAndFilter: listFilter,
        page,
        sizePerPage,
        reload,
        sort: noNewDateView ? NO_NEW_DATE_SORT : OVERDUE_SORT,
    });
    // Tile counts: same filters, one row each (only the count is used).
    const countOf = (t) => ({ filters, extraAndFilter: filterFor(t), page: 0, sizePerPage: 1, reload });
    const allCount = useAppointmentSearch(countOf(TILES.ALL));
    const repeatCount = useAppointmentSearch(countOf(TILES.REPEAT));
    const noKamCount = useAppointmentSearch(countOf(TILES.NO_KAM));
    const noNewDateCount = useAppointmentSearch(countOf(TILES.NO_NEW_DATE));
    // One count per age band (fixed list, so the hook order never changes).
    const bandCounts = NO_NEW_DATE_AGE_BANDS.map((b) => ({ band: b, query: useAppointmentSearch(countOf(b.key)) })); // eslint-disable-line react-hooks/rules-of-hooks
    const noNewDateTotal = noNewDateCount.data?.count ?? 0;
    const pickTile = (t) => { setTile(t); setPage(0); };

    const refresh = () => { setNow(dayjs()); setReload((r) => r + 1); };
    const columns = noNewDateView
        ? buildNoNewDateColumns({ kamByMerchant, kamError, now, onSchedule: (row) => setScheduleRow(row) })
        : buildColumns({ kamByMerchant, kamError, now, onMarkMissed: (row) => setMissRow(row) });
    // /order/search returns the total as `count` (skipCount false).
    // While a tile's list loads, RTK Query still returns the previous tile's rows; the two lists have different
    // columns and actions (Mark as Missed vs Book next appt), so show nothing until the right rows arrive.
    const total = isFetching ? 0 : (searchData?.count ?? 0);
    const rows = !isFetching && searchData?.data?.length > 0 ? searchData.data : [];

    return (
        <>
            <PageTitle>Overdue Appointments</PageTitle>
            {isFetching && <Loader />}

            <SectionTitle title='Slot passed, not closed' hint='open appointment from an earlier day, never marked as missed' />
            <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={12} md={4}>
                    <KpiCard color='red' label='Overdue, Not Closed' value={allCount.data?.count ?? 0} loading={allCount.isFetching}
                        sub='All overdue · click to show all' icon={<ReportProblemIcon />}
                        selected={tile === TILES.ALL} onClick={() => pickTile(TILES.ALL)}
                        info='Open appointments from an earlier day that were never marked as missed. Delivered, RTO and cancelled shipments are not listed.' />
                </Grid>
                <Grid item xs={12} md={4}>
                    <KpiCard color='amber' label='Repeat Misses' value={repeatCount.data?.count ?? 0} loading={repeatCount.isFetching}
                        sub='Missed before · close these first' icon={<RepeatIcon />}
                        selected={tile === TILES.REPEAT} onClick={() => pickTile(TILES.REPEAT)}
                        info='Overdue appointments on shipments that already have at least one missed appointment (Appt #2 or later).' />
                </Grid>
                {!kamError && (
                    <Grid item xs={12} md={4}>
                        <KpiCard color='purple' label='No KAM Mapped' value={noKamCount.data?.count ?? 0}
                            loading={!kamLoaded || noKamCount.isFetching}
                            sub='Nobody to chase · map on PTL Team Master' icon={<PersonOffIcon />}
                            selected={tile === TILES.NO_KAM} onClick={() => pickTile(TILES.NO_KAM)}
                            info='Overdue appointments of merchants with no KAM in the merchant → KAM mapping (PTL Team Master).' />
                    </Grid>
                )}
            </Grid>

            <SectionTitle title='Missed, no new date' hint='marked as missed with a reason, no fresh appointment booked · age counted from the missed appointment date' />
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={2.4}>
                    <KpiCard color='red' label='No New Date · All' value={noNewDateTotal} loading={noNewDateCount.isFetching}
                        sub='Click to show all' icon={<AccessTimeIcon />}
                        selected={tile === TILES.NO_NEW_DATE} onClick={() => pickTile(TILES.NO_NEW_DATE)}
                        info='Shipments marked as missed with a reason whose next appointment is not booked yet. Delivered, RTO and cancelled shipments are not listed.' />
                </Grid>
                {bandCounts.map(({ band, query }, i) => {
                    const value = query.data?.count ?? 0;
                    return (
                        <Grid item xs={12} sm={6} md={2.4} key={band.key}>
                            <KpiCard color={band.kpiColor} label={band.label} value={value} loading={query.isFetching}
                                valueColor={band.color} icon={i >= 2 ? <WarningAmberIcon /> : <AccessTimeIcon />}
                                sub={<><PctChip value={value} total={noNewDateTotal} color={band.color} bg={band.bg} />{band.hint}</>}
                                selected={tile === band.key} onClick={() => pickTile(band.key)}
                                info={`Missed, no new date, last missed appointment ${band.label} ago.`} />
                        </Grid>
                    );
                })}
            </Grid>
            <ShareBar
                total={noNewDateTotal}
                segments={bandCounts.map(({ band, query }) => ({
                    key: band.key, label: band.label, color: band.color, value: query.data?.count ?? 0,
                }))}
            />

            <PageBanner variant='info' icon={<ReportProblemIcon />} title='Close these, then book the next appointment'>
                <strong>Slot passed, not closed:</strong> an appointment still open on any day after its date is overdue
                (an appointment on 12 Oct shows here from 13 Oct). Use <strong>Mark as Missed</strong> to record the failure
                reason and attribution; the shipment then moves to Missed &amp; Repeat and the next appointment can be booked.
                {' '}<strong>Missed, no new date:</strong> the reason is recorded but nobody has booked a fresh slot yet; use
                {' '}<strong>Book next appt</strong>.
            </PageBanner>

            <FilterBar
                show={['merchant', 'courier', 'awb']}
                onApply={({ fromDate, toDate, ...f }) => { setFilters(f); setPage(0); refresh(); }}
                onReset={() => { setFilters({}); setPage(0); refresh(); }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
                <Typography variant='caption' sx={{ color: '#888' }}>
                    {noNewDateView ? 'Missed, no new date' : 'Slot passed, not closed'} · oldest first · checked at {now.format('h:mm A, D MMM')}
                </Typography>
                <Button size='small' startIcon={<RefreshIcon />} onClick={refresh}>Refresh</Button>
                <Box sx={{ ml: 'auto' }}>
                    <ExportButton
                        label='Export'
                        filters={filters}
                        extraAndFilter={listFilter}
                        fileName={noNewDateView ? 'ABD_Missed_No_New_Date' : 'ABD_Overdue_Appointments'}
                    />
                </Box>
            </Box>

            {!isFetching && total === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#fff', borderRadius: 2 }}>
                    <InboxIcon sx={{ fontSize: 28, color: '#bdbdbd' }} />
                    <Typography sx={{ color: '#666', mb: 1 }}>
                        {noNewDateView ? 'No missed shipments waiting for a new date for the selected filters.' : 'No overdue appointments for the selected filters.'}
                    </Typography>
                    <Button size='small' component={Link} to='/appointments/all'>Go to All Appointments</Button>
                </Box>
            ) : (
                <ExpandableAppointmentsTable
                    columns={columns}
                    rows={rows}
                    page={page}
                    sizePerPage={sizePerPage}
                    setPage={setPage}
                    setSizePerPage={setSizePerPage}
                    rowCount={total}
                    getRowId={(row) => row.id}
                />
            )}

            <MUIModal
                open={!!missRow}
                handleClose={() => setMissRow(null)}
                maxWidth='md'
                title={
                    <Typography variant='subtitle1' sx={{ display: 'flex', alignItems: 'center' }}>
                        Mark as Missed — AWB:{' '}
                        <Typography component='span' color='error' fontWeight='bold'>{missRow?.awb_number}</Typography>
                        <CopyIcon text={missRow?.awb_number} />
                    </Typography>
                }
            >
                <MarkMissedModal
                    rowData={missRow}
                    onClose={() => setMissRow(null)}
                    onDone={refresh}
                    onScheduleNext={(row) => setScheduleRow(row)}
                />
            </MUIModal>

            <MUIModal
                open={!!scheduleRow}
                handleClose={() => setScheduleRow(null)}
                maxWidth='lg'
                hideClose
                title={
                    <Typography variant='subtitle1' sx={{ display: 'flex', alignItems: 'center' }}>
                        Schedule Appointment — AWB:{' '}
                        <Typography component='span' color='error' fontWeight='bold'>{scheduleRow?.awb_number}</Typography>
                        <CopyIcon text={scheduleRow?.awb_number} />
                    </Typography>
                }
            >
                <AppointmentDelivery
                    rowData={scheduleRow}
                    setDelConfirm={() => setScheduleRow(null)}
                    setReload={refresh}
                />
            </MUIModal>
        </>
    );
}
